import React, { useState, useContext } from 'react';
import { BookmarksContext } from '../contexts/BookmarksContext';
import { useDisplayPreferences } from '../contexts/DisplayPreferencesContext';
import { getFormattedProductPrice } from '../utils/priceFormatter';
import { FONT_BODY, FONT_FOOTNOTE } from '../constants/typography';
import { BORDER_RADIUS, SPACING_ELEMENT, SPACING_COMPACT } from '../constants/layout';

export default function ProductGridItem({ product, coverImage, onClick, isNew }) {
  const [loaded, setLoaded] = useState(false);
  const { isBookmarked } = useContext(BookmarksContext);
  const { showPrices, showNewBadge } = useDisplayPreferences();
  const formattedPrice = getFormattedProductPrice(product);

  return (
    <button className="product-grid-btn" onClick={onClick}>
      <div className={`product-image-container${coverImage && coverImage.imageURL && !loaded ? ' shimmer' : ''}`}>
        {coverImage && coverImage.imageURL ? (
          <img
            src={coverImage.thumbnailURL || coverImage.imageURL}
            alt={product.productName}
            onLoad={() => setLoaded(true)}
            className={`product-grid-img ${loaded ? 'loaded' : ''}`}
          />
        ) : (
          <div className="product-placeholder">
            <span className="material-icons">image</span>
          </div>
        )}
        {showNewBadge && isNew && (
          <span className="grid-new-badge">New</span>
        )}
        {isBookmarked(product.id) && (
          <span className="grid-bookmark-icon material-icons">bookmark</span>
        )}
      </div>
      <div className="product-grid-name">{product.productName}</div>
      {showPrices && formattedPrice && (
        <div className="product-grid-price">{formattedPrice}</div>
      )}
      <style jsx>{`
        .product-grid-btn {
          display: flex;
          flex-direction: column;
          align-items: stretch;
          padding: 0;
          border: none;
          background: none;
          cursor: pointer;
          transition: transform 0.2s ease;
          text-align: left;
        }
        .product-grid-btn:hover {
          transform: translateY(-2px);
        }
        .product-grid-btn:focus {
          outline: 2px solid var(--color-link);
          outline-offset: 2px;
        }
        .product-image-container {
          position: relative;
          width: 100%;
          box-sizing: border-box;
          overflow: hidden;
          border: 1px solid var(--color-border-strong);
          border-radius: ${BORDER_RADIUS};
          background: #3a3a3a;
        }
        .product-image-container.shimmer {
          background: linear-gradient(90deg, #3a3a3a 25%, #4a4a4a 50%, #3a3a3a 75%);
          background-size: 200% 100%;
          animation: shimmer 1.5s ease-in-out infinite;
        }
        .product-image-container::before {
          content: '';
          display: block;
          padding-top: 100%;
        }
        .product-grid-img {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          display: block;
          object-fit: cover;
          opacity: 0;
          transition: opacity 0.5s ease-in;
        }
        .product-grid-img.loaded {
          opacity: 1;
        }
        .product-placeholder {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #3a3a3a;
          color: var(--color-text-muted);
        }
        .product-placeholder .material-icons {
          font-size: 40px;
        }
        .grid-new-badge {
          position: absolute;
          top: ${SPACING_ELEMENT};
          left: ${SPACING_ELEMENT};
          background-color: var(--color-accent);
          color: var(--color-accent-text);
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.04em;
          padding: 2px 8px;
          border-radius: 4px;
          z-index: 1;
          pointer-events: none;
        }
        .grid-bookmark-icon {
          position: absolute;
          top: ${SPACING_ELEMENT};
          right: ${SPACING_ELEMENT};
          color: red;
          font-size: 20px;
          z-index: 1;
          pointer-events: none;
        }
        .product-grid-name {
          margin-top: 0px;
          padding-top: ${SPACING_COMPACT};
          font-size: ${FONT_BODY};
          color: var(--color-text-primary);
          text-align: left;
          word-break: break-word;
          font-weight: 600;
          line-height: 1.3;
        }
        .product-grid-price {
          margin-top: 4px;
          padding-bottom: ${SPACING_COMPACT};
          font-size: ${FONT_FOOTNOTE};
          color: var(--color-text-secondary);
          font-weight: 400;
          text-align: left;
        }
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </button>
  );
}
